import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuthInstance } from "../firebase";
import SignIn from "../SignIn";
import { AppLoader } from "../components/AppLoader";
import { FaCheck } from "react-icons/fa6";

const slides = [
  {
    id: 1,
    backgroundColor: '#FAF3A8',
    image: "https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2FInstant-Insights-Growth.svg?alt=media&token=8e8c4044-ee93-4121-b21f-b05a81a894a6",
    title: "Insights that Sell",
    description: "PollBolt turns responses into clear insights and actions.",
    features: [
      "Instant dashboards that highlight what matters",
      "AI Research Assistant that explains the \"why\"",
      "From feedback to growth without extra tools"
    ]
  },
  {
    id: 2,
    backgroundColor: '#E6F4D7',
    image: "https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2FDesign-that-Converts.png?alt=media&token=49445a8f-00c3-491b-a3cc-af008b2c8827",
    title: "Design that Converts",
    description: "Brand every form so it feels like you.",
    features: [
      "Upload logo and match your brand colors",
      "Add visuals that increase response rates",
      "Share with a unique branded link"
    ]
  },
  {
    id: 3,
    backgroundColor: '#DCE0FF',
    image: "https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2FConnected-day-1.svg?alt=media&token=a12c34bc-ea49-40cb-a080-75fafb432ecb",
    title: "Connected from Day One",
    description: "PollBolt works where your business works.",
    features: [
      "Export directly into Google Sheets",
      "Send alerts to Slack & sync with Mailchimp",
      "Keep your stack alive with webhooks & Zapier"
    ]
  }
];

const AuthPage = () => {
  const auth = getAuthInstance();
  const [user, userLoading] = useAuthState(auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(false);
  const [leftImageLoaded, setLeftImageLoaded] = useState(false);
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Preload all images
  useEffect(() => {
    let slidesLoaded = 0;
    let logoLoaded = false;

    const checkAllLoaded = () => {
      if (slidesLoaded >= 2 && logoLoaded) { // We have 2 unique images
        setLeftImageLoaded(true);
        setLogoImageLoaded(true);
      }
    };

    // Fallback timeout - show page after 3 seconds max
    const timeout = setTimeout(() => {
      setLeftImageLoaded(true);
      setLogoImageLoaded(true);
    }, 3000);

    // Get unique images from slides
    const uniqueImages = [...new Set(slides.map(slide => slide.image))];
    
    // Preload unique slide images
    uniqueImages.forEach(imageSrc => {
      const img = new Image();
      img.onload = () => {
        slidesLoaded++;
        checkAllLoaded();
      };
      img.onerror = () => {
        slidesLoaded++;
        checkAllLoaded();
      };
      img.src = imageSrc;
    });

    // Preload logo image
    const logoImg = new Image();
    logoImg.onload = () => {
      logoLoaded = true;
      checkAllLoaded();
    };
    logoImg.onerror = () => {
      logoLoaded = true;
      checkAllLoaded();
    };
    logoImg.src = "https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Fpb-logo.svg?alt=media&token=5ff8f07f-3e6e-484f-97ba-9a03ba76d028";

    return () => clearTimeout(timeout);
  }, []);

  // Get initial mode from URL params
  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "signup") {
      setIsSignUp(true);
    } else {
      setIsSignUp(false);
    }
  }, [searchParams]);

  // Auto-play carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Redirect if user is already logged in
  useEffect(() => {
    if (!userLoading && user && user.emailVerified) {
      const pendingToken = sessionStorage.getItem("pendingInviteToken");
      if (pendingToken) {
        sessionStorage.removeItem("pendingInviteToken");
        navigate(`/invite/${pendingToken}`, { replace: true });
      } else {
        navigate("/workspaces", { replace: true });
      }
    }
  }, [user, userLoading, navigate]);

  const handleClose = () => {
    // Instead of closing modal, redirect to landing page
    navigate("/", { replace: true });
  };

  // Show loader while user is loading OR while images are loading
  if (userLoading || !leftImageLoaded || !logoImageLoaded) {
    return <AppLoader coverage="full" backgroundColor="#FFFFFF" />;
  }

  // If user is already logged in, show loader while redirecting
  if (user && user.emailVerified) {
    return <AppLoader coverage="full" backgroundColor="#FFFFFF" />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop Layout: 1/3 left panel + 2/3 right form */}
      <div className="hidden lg:flex min-h-screen">
        {/* Left Panel - Carousel */}
        <div className="w-1/3 min-h-screen relative overflow-hidden">
          {/* Slides */}
          <div 
            className="flex transition-transform duration-500 ease-in-out h-full"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {slides.map((slide, index) => (
              <div 
                key={slide.id}
                className="w-full min-h-screen flex items-center justify-center p-12 flex-shrink-0"
                style={{ backgroundColor: slide.backgroundColor }}
              >
                <div className="text-left max-w-sm">
                  {/* Image */}
                  <div className="mb-8">
                    <img 
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Title */}
                  <h2 className="font-bold text-gray-900 mb-6 font-inter" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                    {slide.title}
                  </h2>
                  
                  {/* Description */}
                  <p className="text-gray-700 mb-6 leading-relaxed font-inter" style={{ fontSize: '16px' }}>
                    {slide.description}
                  </p>
                  
                  {/* Feature List */}
                  <div className="space-y-4 text-left">
                    {slide.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <FaCheck className="w-4 h-4 text-gray-700 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 font-inter" style={{ fontSize: '14px' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Dots */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  index === currentSlide ? 'bg-gray-800' : 'bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Right Panel - 2/3 of screen with form */}
        <div className="w-2/3 min-h-screen flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <SignIn onClose={handleClose} isSignUp={isSignUp} />
          </div>
        </div>
      </div>

      {/* Mobile Layout: Original centered form */}
      <div className="lg:hidden min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <SignIn onClose={handleClose} isSignUp={isSignUp} />
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
